import React, { useState } from 'react';
import { TrendingDown, TrendingUp, AlertTriangle, Info, ChevronDown, ChevronUp, Check, X, Calendar, Activity, Clock } from 'lucide-react';
import { DetectedChange, MovementDataPoint, MovementMetric, MovementTrend } from './interfaces/types';
import { calculateTrend } from './utils/PatternAnalysisEngine';
import TrendVisualizer from './TrendVisualizer';

interface ChangeDetailCardProps {
  change: DetectedChange;
  allChanges: DetectedChange[];
  movementData: MovementDataPoint[];
  metrics: MovementMetric[];
  onReviewChange: (changeId: string, isReviewed: boolean) => void;
  onAddToCareplan: (changeId: string, isAddressed: boolean) => void;
  expanded?: boolean;
}

const ChangeDetailCard: React.FC<ChangeDetailCardProps> = ({
  change,
  allChanges,
  movementData,
  metrics,
  onReviewChange,
  onAddToCareplan,
  expanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(expanded);
  const [showProjection, setShowProjection] = useState<boolean>(false);
  
  // Get the metric associated with this change
  const metric = metrics.find(m => m.id === change.metricId);
  
  if (!metric) {
    return <div className="p-4 bg-red-100 rounded-lg">Metric data not found</div>;
  }
  
  // Calculate the trend data for visualization
  const trend: MovementTrend = calculateTrend(
    movementData,
    change.metricId,
    change.datePeriod.start,
    change.datePeriod.end
  );
  
  // Get related changes
  const relatedChangeDetails = change.relatedChanges ? 
    allChanges.filter(c => change.relatedChanges?.includes(c.id)) : [];
  
  // Format the date period
  const formatDatePeriod = () => {
    const startDate = change.datePeriod.start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const endDate = change.datePeriod.end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    return `${startDate} - ${endDate}`;
  };
  
  // Get the icon for the change direction
  const getDirectionIcon = () => {
    switch (change.direction) {
      case 'IMPROVING':
        return <TrendingUp className="mr-1 text-green-500" size={20} />;
      case 'DECLINING':
        return <TrendingDown className="mr-1 text-red-500" size={20} />;
      case 'FLUCTUATING':
        return <Activity className="mr-1 text-yellow-500" size={20} />;
      default:
        return <AlertTriangle className="mr-1 text-gray-500" size={20} />;
    }
  };
  
  // Get background color based on significance
  const getBackgroundColor = () => {
    if (change.isReviewed) return 'bg-green-50 border-green-200';
    
    switch (change.significance) {
      case 'SIGNIFICANT':
        return change.direction === 'DECLINING' ? 
          'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
      case 'MODERATE':
        return change.direction === 'DECLINING' ? 
          'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200';
      case 'MINOR':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };
  
  return (
    <div className={`mb-4 rounded-lg border ${getBackgroundColor()} overflow-hidden transition-all duration-200`}>
      {/* Header section - always visible */}
      <div className="p-4 flex justify-between items-center">
        <div className="flex items-center">
          {getDirectionIcon()}
          <span className="font-medium">{change.metricName}</span>
          <span className="ml-2 text-sm text-gray-600 hidden md:inline">
            {Math.abs(change.percentageChange).toFixed(1)}% {change.direction.toLowerCase()}
          </span>
          {change.isReviewed && (
            <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">
              Reviewed
            </span>
          )}
          {change.isAddressedInCareplan && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">
              In Care Plan
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
            change.significance === 'SIGNIFICANT' ? 'bg-red-100 text-red-800' :
            change.significance === 'MODERATE' ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {change.significance}
          </span>
          
          <span className="text-xs text-gray-500 flex items-center">
            <Calendar size={12} className="mr-1" />
            {formatDatePeriod()}
          </span>
          
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isExpanded ? 
              <ChevronUp size={20} className="text-gray-600" /> : 
              <ChevronDown size={20} className="text-gray-600" />
            }
          </button>
        </div>
      </div>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          {/* Description */}
          <p className="mb-4 text-gray-700">{change.description}</p>
          
          {/* Chart */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium">Trend Analysis</h4>
              <button
                onClick={() => setShowProjection(!showProjection)}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
              >
                {showProjection ? 'Hide Projection' : 'Show Projection'}
              </button>
            </div>
            <TrendVisualizer 
              trend={trend} 
              metric={metric} 
              change={change}
              showProjection={showProjection}
            />
          </div>
          
          {/* Additional information in a two-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Health Implications */}
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <Info size={16} className="mr-1 text-blue-500" />
                Health Implications
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                {change.healthImplications?.map((implication, index) => (
                  <li key={`imp-${index}`}>{implication}</li>
                ))}
              </ul>
            </div>
            
            {/* Recommended Actions */}
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <Activity size={16} className="mr-1 text-purple-500" />
                Recommended Actions
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                {change.recommendedActions?.map((action, index) => (
                  <li key={`action-${index}`}>{action}</li>
                ))}
              </ul>
            </div>
            
            {/* Potential Causes */}
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <AlertTriangle size={16} className="mr-1 text-yellow-500" />
                Potential Causes
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                {change.potentialCauses?.map((cause, index) => (
                  <li key={`cause-${index}`}>{cause}</li>
                ))}
              </ul>
            </div>
            
            {/* Related Changes */}
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <h4 className="text-sm font-medium mb-2 flex items-center">
                <Activity size={16} className="mr-1 text-indigo-500" />
                Related Changes
              </h4>
              {relatedChangeDetails.length > 0 ? (
                <ul className="text-sm text-gray-700 space-y-2">
                  {relatedChangeDetails.map(relatedChange => (
                    <li key={relatedChange.id} className="flex items-center">
                      {relatedChange.direction === 'IMPROVING' ? 
                        <TrendingUp size={14} className="mr-1 text-green-500" /> : 
                        <TrendingDown size={14} className="mr-1 text-red-500" />
                      }
                      <span>{relatedChange.metricName} ({Math.abs(relatedChange.percentageChange).toFixed(1)}%)</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">No related changes detected</p>
              )}
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-end mt-4 space-x-2">
            <button
              onClick={() => onReviewChange(change.id, !change.isReviewed)}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center ${
                change.isReviewed ? 
                'bg-gray-100 text-gray-700 hover:bg-gray-200' : 
                'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {change.isReviewed ? (
                <>
                  <X size={16} className="mr-1" />
                  Mark as Unreviewed
                </>
              ) : (
                <>
                  <Check size={16} className="mr-1" />
                  Mark as Reviewed
                </>
              )}
            </button>
            
            <button
              onClick={() => onAddToCareplan(change.id, !change.isAddressedInCareplan)}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center ${
                change.isAddressedInCareplan ? 
                'bg-gray-100 text-gray-700 hover:bg-gray-200' : 
                'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {change.isAddressedInCareplan ? (
                <>
                  <X size={16} className="mr-1" />
                  Remove from Care Plan
                </>
              ) : (
                <>
                  <Check size={16} className="mr-1" />
                  Add to Care Plan
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeDetailCard; 